type EnvLike = Record<string, string | boolean | undefined>;

type UmamiDisabledConfig = {
  enabled: false;
};

type UmamiEnabledConfig = {
  enabled: true;
  websiteId: string;
  src: string;
};

export type UmamiConfig = UmamiDisabledConfig | UmamiEnabledConfig;

const DEFAULT_UMAMI_SRC = 'https://cloud.umami.is/script.js';

function getSafeUmamiSrc(src: string | boolean | undefined): string {
  if (typeof src !== 'string' || !src.trim()) {
    return DEFAULT_UMAMI_SRC;
  }

  try {
    const url = new URL(src.trim());
    return url.protocol === 'https:' ? url.toString() : DEFAULT_UMAMI_SRC;
  } catch {
    return DEFAULT_UMAMI_SRC;
  }
}

export function getUmamiConfig(env: EnvLike): UmamiConfig {
  const websiteId = typeof env.VITE_UMAMI_WEBSITE_ID === 'string'
    ? env.VITE_UMAMI_WEBSITE_ID.trim()
    : '';

  if (!websiteId) {
    return { enabled: false };
  }

  const src = getSafeUmamiSrc(env.VITE_UMAMI_SRC);

  return {
    enabled: true,
    websiteId,
    src,
  };
}

export function createUmamiScript(config: UmamiConfig): HTMLScriptElement | null {
  if (!config.enabled) {
    return null;
  }

  const script = document.createElement('script');
  script.defer = true;
  script.src = config.src;
  script.setAttribute('data-website-id', config.websiteId);

  return script;
}

export function installUmamiAnalytics(env: EnvLike = import.meta.env): void {
  if (typeof document === 'undefined') {
    return;
  }

  const config = getUmamiConfig(env);

  if (!config.enabled) {
    return;
  }

  const existingScript = Array.from(document.querySelectorAll('script')).some((existing) => (
    existing.getAttribute('data-website-id') === config.websiteId
    && existing.getAttribute('src') === config.src
  ));

  if (existingScript) {
    return;
  }

  const script = createUmamiScript(config);
  if (script) {
    document.head.appendChild(script);
  }
}
